import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaArrowLeft, 
  FaArrowRight,
  FaStar
} from 'react-icons/fa';
import { sliderData } from '../../data/sliderData';

const Slider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const { slides } = sliderData;

  // Auto play functionality
  useEffect(() => {
    let interval;
    if (isAutoPlaying) {
      interval = setInterval(() => {
        nextSlide();
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [currentSlide, isAutoPlaying]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Pause auto play on hover
  const pauseAutoPlay = () => setIsAutoPlaying(false);
  const resumeAutoPlay = () => setIsAutoPlaying(true);

  return (
    <section 
      className="relative w-full overflow-hidden"
      onMouseEnter={pauseAutoPlay}
      onMouseLeave={resumeAutoPlay}
    >
      {/* Main Slider */}
      <div className="relative">
        {/* Previous Button */}
        <button 
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-30 flex items-center justify-center w-10 h-10 transition-all border rounded-full bg-white/20 backdrop-blur-sm border-white/30 hover:bg-white/30"
          aria-label="Previous slide"
        >
          <FaArrowLeft className="text-white" size={16} />
        </button>

        {/* Slides Container */}
        <div className="relative h-[450px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.6 }}
              className="absolute inset-0"
            >
              <div className="relative h-full w-full">
                {/* Background Image */}
                <div className="absolute inset-0">
                  <img
                    src={slides[currentSlide].image}
                    alt={`Slide ${currentSlide + 1}`}
                    className="object-cover w-full h-full"
                    loading={currentSlide === 0 ? "eager" : "lazy"}
                  />
                  {/* Gradient Overlay */}
                  <div className={`absolute inset-0 bg-gradient-to-r ${slides[currentSlide].gradient}`} />
                  <div className="absolute inset-0 bg-black/30" />
                </div>

                {/* Content */}
                <div className="relative flex items-center h-full">
                  <div className="container px-4 mx-auto sm:px-6">
                    <div className="max-w-2xl">
                      <motion.div 
                        className="space-y-5"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.5 }}
                      >
                        {/* Badge */}
                        <div className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1.5 border border-white/20">
                          <div className="flex items-center gap-0.5">
                            {[...Array(5)].map((_, i) => (
                              <FaStar key={i} className="w-2.5 h-2.5 text-amber-300" />
                            ))}
                          </div>
                          <span className="text-xs font-medium text-white">
                            {slides[currentSlide].badge.text}
                          </span>
                        </div>

                        {/* Title */}
                        <h1 className="text-3xl font-bold leading-snug text-white md:text-4xl lg:text-5xl">
                          {slides[currentSlide].title.prefix}{' '}
                          <span className={`bg-gradient-to-r ${slides[currentSlide].title.highlightColor} bg-clip-text text-transparent`}>
                            {slides[currentSlide].title.highlight}
                          </span>
                        </h1>

                        {/* Description */}
                        <p className="max-w-xl text-base leading-relaxed md:text-lg text-white/90">
                          {slides[currentSlide].description}
                        </p>

                        {/* Stats */}
                        <div className="flex flex-wrap gap-4 pt-2">
                          {slides[currentSlide].stats.map((stat, index) => (
                            <div key={index} className="text-center">
                              <div className={`text-xl font-bold bg-gradient-to-r ${slides[currentSlide].badge.color} bg-clip-text text-transparent`}>
                                {stat.value}
                              </div>
                              <div className="text-xs font-medium text-white/70">
                                {stat.label}
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Buttons */}
                        <div className="flex flex-col gap-3 pt-3 sm:flex-row">
                          {slides[currentSlide].buttons.map((button, index) => (
                            button.isOutline ? (
                              <button
                                key={index}
                                className="px-6 py-2.5 bg-white/10 backdrop-blur-sm text-white font-semibold rounded-lg border border-white/30 hover:bg-white/15 transition-all duration-200 text-sm"
                              >
                                {button.text}
                              </button>
                            ) : (
                              <button
                                key={index}
                                className={`px-6 py-2.5 bg-gradient-to-r ${button.gradient} text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-1.5 text-sm group`}
                              >
                                {button.text}
                                {button.hasIcon && (
                                  <button.icon className="group-hover:translate-x-0.5 transition-transform text-sm" />
                                )}
                              </button>
                            )
                          ))}
                        </div>
                      </motion.div>
                    </div>
                  </div>
                </div>

                {/* Slide Counter - Only show on mobile */}
                <div className="absolute px-3 py-1 rounded-full top-4 right-4 bg-black/30 backdrop-blur-sm md:hidden">
                  <span className="text-sm font-medium text-white">
                    <span className="text-lg">{currentSlide + 1}</span>
                    <span className="mx-1 text-white/60">/</span>
                    <span className="text-base text-white/80">{slides.length}</span>
                  </span>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Next Button */}
        <button 
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-30 flex items-center justify-center w-10 h-10 transition-all border rounded-full bg-white/20 backdrop-blur-sm border-white/30 hover:bg-white/30"
          aria-label="Next slide"
        >
          <FaArrowRight className="text-white" size={16} />
        </button>

        {/* Dots Navigation - Desktop only */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 gap-2 hidden md:flex">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className="focus:outline-none"
              aria-label={`Go to slide ${index + 1}`}
            >
              <div className="relative">
                <div className={`h-1.5 rounded-full transition-all duration-300 ${
                  currentSlide === index ? 'w-10 bg-white' : 'w-6 bg-white/50 hover:bg-white/70'
                }`}>
                  {currentSlide === index && (
                    <motion.div 
                      className="h-full rounded-full bg-gradient-to-r from-blue-400 to-cyan-300"
                      initial={{ width: '0%' }}
                      animate={{ width: '100%' }}
                      transition={{ duration: 5, ease: 'linear' }}
                    />
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Bottom Dots - Mobile only */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-1.5 md:hidden">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`h-2 rounded-full transition-all duration-200 ${
              currentSlide === index ? 'w-8 bg-white' : 'w-2 bg-white/50 hover:bg-white/70'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default Slider;